/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { CacheType, getCatalogId, IAny, ICreateActionContext, IGeneric, IRequestContext } from '@msdyn365-commerce/core';
import { BypassCache, ProductDimension, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getSupersessionAsync } from './DataActionExtension.g';

/**
 * Stores data about the selected product.
 */
export interface ISelectedProduct extends SimpleProduct {
    productVariant?: SimpleProduct;
}
/**
 * GetSupersessionProducts Input Action
 */
export class GetSupersessionProductsInput implements Msdyn365.IActionInput {
    public productId: number;

    public channelId: number;

    public matchingDimensionValues?: ProductDimension[];

    public bypassCache?: BypassCache;

    public catalogId?: number;
    public constructor(
        productId: number,
        channelId: number,
        matchingDimensionValues?: ProductDimension[],
        bypassCache?: BypassCache,
        requestContext?: IRequestContext
    ) {
        this.productId = productId;
        this.channelId = channelId;
        this.matchingDimensionValues = matchingDimensionValues;
        this.bypassCache = bypassCache;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => 'SelectedVariant';

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType: () => string = () => 'SimpleProduct';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'none';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetSupersessionProductsData {
    productVariant?: SimpleProduct;
}

export const getSelectedProductIdFromActionInput = (inputData: ICreateActionContext<IGeneric<IAny>>): string | undefined => {
    const context = inputData.requestContext;
    if (context.query?.productId) {
        return context.query.productId;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Exsiting code.
    } else if (
        context.urlTokens &&
        inputData.requestContext.urlTokens.itemId &&
        inputData.requestContext.urlTokens.pageType === 'Product'
    ) {
        return context.urlTokens.itemId;
    } else if (inputData && inputData.config && inputData.config.productId) {
        return inputData.config.productId;
    }
    return undefined;
};

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetSupersessionProductsInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new GetSupersessionProductsInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            undefined,
            undefined,
            inputData.requestContext
        );
    }
    throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function getSupersessionProductAction(input: GetSupersessionProductsInput, ctx: Msdyn365.IActionContext): Promise<ISelectedProduct> {
    const result = await getSupersessionAsync({ callerContext: ctx }, [], [+input.productId]);
    if (result) {
        return { ...result[0] };
    }
    throw new Error('Unable to get product');
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ISelectedProduct>>getSupersessionProductAction,
    // TODO: Give your data action a unique id
    id: '@msdyn365-commerce-modules/retail-actions/get-selected-variant',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
